/**
 * Feldregeln der Einstellungen (zod). Sie spiegeln die Prüfung des Servers nur so weit, wie sie ohne
 * Rückfrage entscheidbar ist; maßgeblich bleibt `/validate`.
 */
import { z } from 'zod';
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';

const decimal = z.string().refine(
  (entry) => {
    if (entry === '') return true;
    const number = Number(entry);
    return number > 0 && number <= 99_999_999.99 && /^\d+(\.\d{1,2})?$/.test(entry);
  },
  { message: 'money' },
);
const integer = (maximum: number, optional = false) =>
  z
    .string()
    .refine(
      (entry) =>
        (optional && entry === '') ||
        (Number.isInteger(Number(entry)) && Number(entry) >= 1 && Number(entry) <= maximum),
      { message: maximum === 30 ? 'range30' : 'range500' },
    );
const nonNegativeInteger = z
  .string()
  .refine((entry) => Number.isSafeInteger(Number(entry)) && Number(entry) >= 0, {
    message: 'nonNegativeInteger',
  });
const time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'time' });

export const pricingConfigurationFormSchema: z.ZodType<PricingConfigurationFormValues> = z
  .object({
    mode: z.enum(['Off', 'MinimumStayOnly', 'Automatic']),
    minimumPrice: decimal,
    referencePrice: decimal,
    maximumPrice: decimal,
    strategyProfileVersionId: z.string(),
    regularMinimumStay: integer(30, true),
    fridaySaturdayMinimumStay: integer(30, true),
    minimumStayTiersEnabled: z.boolean(),
    minimumStayLeadTimeTiers: z
      .array(
        z.object({
          daysBeforeArrival: nonNegativeInteger,
          minimumStayNights: integer(30),
        }),
      )
      .max(5, { message: 'tiersMax5' }),
    maximumGapLength: integer(30),
    gapPolicy: z.enum(['Always', 'WithinDays']),
    gapWithinDays: integer(500, true),
    lastMinuteDiscountEnabled: z.boolean(),
    gapDiscountEnabled: z.boolean(),
    calculationIntervalHours: z
      .string()
      .refine((entry) => ['1', '3', '6', '12', '24'].includes(entry), { message: 'interval' }),
    calculationStartLocalTime: time,
    publicationWindows: z
      .array(z.object({ startLocalTime: time, endLocalTime: time }))
      .min(1, { message: 'publicationRequired' }),
  })
  .superRefine((values, context) => {
    const minimum = values.minimumPrice === '' ? null : Number(values.minimumPrice);
    const reference = values.referencePrice === '' ? null : Number(values.referencePrice);
    const maximum = values.maximumPrice === '' ? null : Number(values.maximumPrice);
    if (minimum !== null && reference !== null && minimum > reference) {
      context.addIssue({
        code: 'custom',
        path: ['minimumPrice'],
        message: 'minimumAboveReference',
      });
    }
    if (reference !== null && maximum !== null && reference > maximum) {
      context.addIssue({
        code: 'custom',
        path: ['referencePrice'],
        message: 'referenceAboveMaximum',
      });
    }
    if (values.gapPolicy === 'WithinDays' && values.gapWithinDays === '') {
      context.addIssue({ code: 'custom', path: ['gapWithinDays'], message: 'required' });
    }
    if (values.minimumStayTiersEnabled && values.mode === 'Off') {
      context.addIssue({
        code: 'custom',
        path: ['minimumStayTiersEnabled'],
        message: 'tierRequiresActiveMode',
      });
    }
    const thresholds = new Set<number>();
    values.minimumStayLeadTimeTiers.forEach((tier, index) => {
      const threshold = Number(tier.daysBeforeArrival);
      if (thresholds.has(threshold)) {
        context.addIssue({
          code: 'custom',
          path: ['minimumStayLeadTimeTiers', index, 'daysBeforeArrival'],
          message: 'tierThresholdUnique',
        });
      }
      thresholds.add(threshold);
    });
    const ordered = values.publicationWindows
      .map((window, index) => ({ ...window, index }))
      .sort((left, right) => left.startLocalTime.localeCompare(right.startLocalTime));
    ordered.forEach((window) => {
      if (window.startLocalTime >= window.endLocalTime) {
        context.addIssue({
          code: 'custom',
          path: ['publicationWindows', window.index, 'endLocalTime'],
          message: 'publicationOrder',
        });
      }
    });
    ordered.slice(1).forEach((window, index) => {
      if (ordered[index] && ordered[index]!.endLocalTime > window.startLocalTime) {
        context.addIssue({
          code: 'custom',
          path: ['publicationWindows', window.index, 'startLocalTime'],
          message: 'publicationOverlap',
        });
      }
    });
  });
